import { ErrorCode } from './types';

/**
 * Raised when acquiring an access token was cancelled/interrupted (e.g. the app
 * had to redirect to the identity provider to re-authenticate).
 */
export class AuthTokenCancelledError extends Error {
  public static errorCode: ErrorCode = 'auth_token_cancelled';

  get errorCode() {
    return AuthTokenCancelledError.errorCode;
  }

  constructor() {
    super('Auth token call has been cancelled. New token needs to be retrieved');
  }
}

type AccessTokenProvider = () => Promise<string>;
type RefreshTokenHandler = () => Promise<void>;
type UnauthorizedHandler = () => Promise<void>;

/**
 * The api layer is auth-agnostic. Authentication (Azure AD B2C / MSAL) is wired
 * in a later task via {@link configureAuthTokenProvider}. Until then the default
 * provider returns an empty token, so authenticated requests simply go out
 * unauthenticated (the private app is gated behind a placeholder for now).
 */
let accessTokenProvider: AccessTokenProvider = async () => '';
let refreshTokenHandler: RefreshTokenHandler = async () => {};
let unauthorizedHandler: UnauthorizedHandler = async () => {};

export function configureAuthTokenProvider(handlers: {
  getAccessToken: AccessTokenProvider;
  refreshToken?: RefreshTokenHandler;
  onUnauthorized?: UnauthorizedHandler;
}) {
  accessTokenProvider = handlers.getAccessToken;
  if (handlers.refreshToken) {
    refreshTokenHandler = handlers.refreshToken;
  }
  if (handlers.onUnauthorized) {
    unauthorizedHandler = handlers.onUnauthorized;
  }
}

export function getAccessToken(): Promise<string> {
  return accessTokenProvider();
}

export function refreshToken(): Promise<void> {
  return refreshTokenHandler();
}

export function handleUnauthorized(): Promise<void> {
  return unauthorizedHandler();
}
